/**
 * Where the card stands, away from every node its cue describes.
 *
 * Pure, and in its own file, because it is the half of the popup a test can
 * hold: the tick in `Coach.tsx` MEASURES — the stage, the columns the panes
 * take, the card's own box, the chrome that bounds it vertically — and every
 * decision made from those numbers is here, where "a laptop with the rail open"
 * is three numbers that can be written down and asserted (`coachPlace.test.ts`).
 * The vertical band is still measured over there, against live chrome, and
 * handed in as a function of where the card would stand.
 *
 * One rule holds the whole file together: the card must never end up standing
 * on the nodes it is about. It is glass that takes presses, so a card over the
 * node its cue is about hides that node and swallows the clicks meant for it —
 * the pins have the same problem and answer it by HIDING, which a card cannot do
 * because the card is the reading itself. So it moves instead: sideways first
 * (`cardX`, decided against the same field the clamp is), and then on both axes
 * against the anchor AND the other nodes the cue names (`cardPlace`) — a study
 * watched the card open over exactly the claims it was describing.
 */

/** The gap between the node and the card, and the exact span of the hairline
 *  that crosses it. One number, so the connector always lands on the anchor. */
export const CARD_GAP = 40

/** The chrome's own margin, the same 16 the bar and the composer keep. */
export const CARD_EDGE = 16

/**
 * Left of this share of the FIELD, the card prefers to fly RIGHT of its node.
 *
 * The field — the stage minus whatever columns the two panes are taking — and
 * not the stage itself. Measured against the full width, this preference says
 * "there is room on the right" for anchors that have none: the clamp below then
 * pushes the card back over its own anchor, and a static 5% of the width cannot
 * compensate a 398px pane. The two computations read one number now.
 *
 * Still below the midpoint, and now for one reason: a pane opening shrinks the
 * field, so every anchor between the shut and open thresholds changes sides as
 * the glass arrives. A threshold under half makes that band narrower, and a
 * card that has already chosen the left side does not move when the pane does.
 */
export const CARD_FLIP_AT = 0.45

/**
 * The window a card's left edge may sit in: never off the left margin, and
 * never under the sidebar's or the thread's glass.
 *
 * The ceiling is resolved before the floor is applied, so a field narrower than
 * the card pins it to the left margin rather than inverting the range. That
 * margin is the sidebar's own column plus the chrome's 16 — with the sidebar
 * collapsed to its rail, or absent at phone width, the inset is smaller or zero
 * and this is the same expression it always was.
 */
export function clampCardX(
  left: number,
  stageW: number,
  insetLeft: number,
  insetRight: number,
  cardW: number,
): number {
  const minLeft = insetLeft + CARD_EDGE
  const maxLeft = Math.max(minLeft, stageW - insetRight - CARD_EDGE - cardW)
  return Math.min(Math.max(left, minLeft), maxLeft)
}

/** Which side of the node the card flies to, and the left edge it lands on. */
export interface CardX {
  side: 'left' | 'right'
  left: number
}

/**
 * The placement, from the anchor and the three widths around it.
 *
 * Both candidates are clamped first and then asked the only question that
 * matters — does this one cover the anchor? — because a side is only available
 * if it survives the clamp. `CARD_FLIP_AT` is the preference between two
 * candidates that both clear the node; it is never allowed to choose one that
 * does not.
 *
 * Where NEITHER clears it, the preference decides after all and the card stands
 * on its anchor. That is arithmetic rather than a choice: clearing the node on
 * one side wants `CARD_GAP + cardW + CARD_EDGE` — 356px for a 300px card — on
 * that side of it, so a field under about 632px cannot offer it to an anchor
 * anywhere near the middle. Real configurations reach that: the stage at the
 * breakpoint itself with the thread open, where a 400px pane leaves ~350px of
 * field for a 300px card — and a laptop carrying both panes at once. The card
 * stays inside the field, which is the one thing still worth holding there —
 * sliding under the glass would lose the reading as well as the node. The pins
 * covered by it hide (`CoachPins.tsx`), so nothing is left drawn-but-unpressable
 * underneath.
 */
export function cardX(
  anchorX: number,
  stageW: number,
  insetLeft: number,
  insetRight: number,
  cardW: number,
): CardX {
  const right = clampCardX(anchorX + CARD_GAP, stageW, insetLeft, insetRight, cardW)
  const left = clampCardX(anchorX - CARD_GAP - cardW, stageW, insetLeft, insetRight, cardW)
  const rightCovers = right <= anchorX
  const leftCovers = left + cardW >= anchorX
  // The share is of the field, so it is measured FROM the field's left edge.
  // Read from 0 with a left pane open, the threshold sits inside the sidebar's
  // own column and every anchor in the field is "past" it — one pane would have
  // sent every card left.
  const preferRight = anchorX < insetLeft + (stageW - insetLeft - insetRight) * CARD_FLIP_AT
  const side =
    rightCovers && !leftCovers
      ? 'left'
      : leftCovers && !rightCovers
        ? 'right'
        : preferRight
          ? 'right'
          : 'left'
  return { side, left: side === 'right' ? right : left }
}

/* ── both axes, and every node the cue describes ─────────────────────────── */

/** The floor on the card's height. Below this a scroller is not a card any more,
 *  so a very short stage lets the box run past the bottom margin instead. */
export const CARD_MIN_H = 180

/**
 * How far the card's box keeps from a node it describes: the node's own glyph,
 * and the numbered pin that sits 18px up and to the right of it (`CoachPins`).
 * A card edge inside this reach hides the pin, the node, or both.
 */
export const NODE_CLEAR = 24

/**
 * What a DOCKED card — the phone's row in the run-done column, which cannot step
 * aside the way the popup does — leaves free between its bottom edge and the
 * composer, so the camera has somewhere to put the node the card is about: the
 * node centred in it, its pin above and one line of its caption below, each
 * inside `NODE_CLEAR`. Every pixel of it comes off the card, which scrolls inside
 * itself for the rest — on a phone that is also showing the run-done bar, the
 * last line or so of a long cue — and that cost is accepted: a card that reaches
 * the composer stands on the claim it is describing.
 */
export const DOCK_ROOM = 2 * NODE_CLEAR + 16

/** A node's rendered position on the stage, in the tick's coordinates. */
export interface Point {
  x: number
  y: number
}

/** The vertical band a card may stand in, measured against the chrome it would
 *  otherwise be buried under. It depends on WHERE the card stands — the chrome it
 *  meets is the chrome in its own column — so it is asked per candidate. */
export interface Band {
  ceil: number
  floor: number
}

/**
 * Where the card can stand: beside its node on either side, directly below or
 * above it, or docked in one of the field's four corners.
 *
 * The beside positions are the design's own — close to the node, with the
 * hairline that points at it — and they come first. The rest exist because a cue
 * is about more than its anchor: the objection, the newer record, the far end of
 * a join (`Cue.alsoIds`) sit around it, and a card placed beside the anchor with
 * only the anchor in mind stood on those instead — and, on a field too narrow to
 * clear the node sideways, on the anchor itself.
 */
export type CardSpot =
  'right' | 'left' | 'below' | 'above' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export interface CardPlacement {
  spot: CardSpot
  left: number
  top: number
  /** The height ceiling the card is held to at this spot. */
  maxH: number
  /** What the placement still covers, where the field left no clean choice. */
  coversAnchor: boolean
  coversAlso: number
}

/**
 * The placement, from the anchor, the other nodes the cue describes, and the
 * field around them.
 *
 * Every candidate is clamped into the field first — never under either pane,
 * never off the band — and then asked the only question the layer cares about:
 * which of the cue's own nodes does this box stand on? The anchor outweighs every
 * other node together, because it is what the card is ABOUT and what the camera
 * walked to. Among candidates that cover equally little, the order decides: the
 * side `cardX` prefers, the other side, below, above, and then the corners
 * nearest the anchor — a card as close to its node as the graph allows.
 *
 * `prev` is the spot the card stood at on the last frame, and it wins any tie
 * with the best. The camera is usually still flying when the card appears, so the
 * answer is re-asked every frame; without this a card that was clear would jump
 * between two equally clear spots as the node slid under it, which reads as the
 * card fleeing the reader rather than the node.
 *
 * A card too tall to stand clear above or below its node at full height may
 * shorten to the room there and scroll inside itself — the one cost ranked below
 * standing on a node, and above every clean full-height spot. Only where the
 * field cannot hold even a shortened card clear — a stage too short and too
 * narrow for `CARD_MIN_H` of card to stand off its node in any direction — does
 * the result still cover something, and then `coversAnchor`/`coversAlso` say what.
 */
export function cardPlace(input: {
  anchor: Point
  also: readonly Point[]
  stageW: number
  insetLeft: number
  insetRight: number
  cardW: number
  /** The card's height with nothing clipping it. */
  cardH: number
  band: (left: number) => Band
  prev?: CardSpot | null
}): CardPlacement {
  const { anchor, also, stageW, insetLeft, insetRight, cardW, cardH, band, prev } = input
  const clampX = (x: number) => clampCardX(x, stageW, insetLeft, insetRight, cardW)
  const preferred = cardX(anchor.x, stageW, insetLeft, insetRight, cardW).side
  const other = preferred === 'right' ? 'left' : 'right'

  const leftOf: Record<CardSpot, number> = {
    right: clampX(anchor.x + CARD_GAP),
    left: clampX(anchor.x - CARD_GAP - cardW),
    below: clampX(anchor.x - cardW / 2),
    above: clampX(anchor.x - cardW / 2),
    'top-left': clampX(-Infinity),
    'bottom-left': clampX(-Infinity),
    'top-right': clampX(Infinity),
    'bottom-right': clampX(Infinity),
  }

  const place = (spot: CardSpot): CardPlacement & { dist: number; shortened: boolean } => {
    const left = leftOf[spot]
    const { ceil, floor } = band(left)
    // Below and above, the card may SHORTEN to the room between the node and the
    // band's edge and scroll inside itself for the rest — the popup's own answer
    // to a short stage, applied where a full-height card would reach back over
    // the node it just stepped off. A field too narrow to clear the node sideways
    // (a laptop carrying both panes) is where this is the only clean spot left.
    const room =
      spot === 'below'
        ? floor - (anchor.y + CARD_GAP)
        : spot === 'above'
          ? anchor.y - CARD_GAP - ceil
          : floor - ceil
    const maxH = Math.max(CARD_MIN_H, Math.min(floor - ceil, room))
    const h = Math.min(cardH, maxH)
    const hold = (top: number) => Math.min(Math.max(top, ceil), Math.max(ceil, floor - h))
    const top =
      spot === 'right' || spot === 'left'
        ? hold(anchor.y - h / 2)
        : spot === 'below'
          ? hold(anchor.y + CARD_GAP)
          : spot === 'above'
            ? hold(anchor.y - CARD_GAP - h)
            : spot === 'top-left' || spot === 'top-right'
              ? hold(ceil)
              : hold(floor - h)
    const on = (p: Point) =>
      p.x > left - NODE_CLEAR &&
      p.x < left + cardW + NODE_CLEAR &&
      p.y > top - NODE_CLEAR &&
      p.y < top + h + NODE_CLEAR
    return {
      spot,
      left,
      top,
      maxH,
      coversAnchor: on(anchor),
      coversAlso: also.filter(on).length,
      dist: Math.hypot(left + cardW / 2 - anchor.x, top + h / 2 - anchor.y),
      shortened: h < Math.min(cardH, Math.max(CARD_MIN_H, floor - ceil)),
    }
  }

  const corners = (['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const)
    .map(place)
    .sort((a, b) => a.dist - b.dist)
  const candidates = ([preferred, other, 'below', 'above'] as const).map(place).concat(corners)
  // Standing on a node outweighs everything; a card that had to shorten to stay
  // clear is the smallest cost there is, so a clean full-height spot anywhere
  // beats it, and it beats standing on anything at all.
  const cost = (c: CardPlacement & { shortened: boolean }) =>
    (c.coversAnchor ? 1000 : 0) + c.coversAlso + (c.shortened ? 0.5 : 0)

  let best = candidates[0]!
  for (const c of candidates) if (cost(c) < cost(best)) best = c
  const held = prev ? candidates.find((c) => c.spot === prev) : undefined
  const { spot, left, top, maxH, coversAnchor, coversAlso } =
    held && cost(held) === cost(best) ? held : best
  return { spot, left, top, maxH, coversAnchor, coversAlso }
}
